import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import StyledHeading from '@/components/StyledHeading'
import SearchFormProvider from '@/context/SearchFormContext'
import type { ProductType } from '@/types'
import { SearchFormTabs } from '@pcln/horizon-search-form'
import { Box } from 'pcln-design-system'
import { useMemo, useState } from 'react'
import useSearchFormTabState from './customHooks/useSearchFormTabState'
import HeroMobile from '../Hotels/Hero/HeroMobile'
import { COMPONENT_ID, PRODUCT_TABS, tabButtons } from './constants'
import useFormatHotelHeadline from './customHooks/useFormatHotelHeadline'
import useHeadlineValues from './customHooks/useHeadlineValues'
import SearchFormDialog from './SearchFormDialog'
import { StyledButton } from './utils/Button.styled'
import {
  fireExitMethodEvent,
  fireInputFieldEvent,
  fireSearchButtonClickEvent,
  fireTabClickEvent,
} from './utils/experimentGA4'
import getProductCategory from './utils/getProductCategory'
import getSearchFormTabsContent from './utils/getSearchFormTabsContent'

export default function SearchFormMobile({
  currentTab,
  onFormChange,
  showNewMobileTabs,
  showMultiHotel = false,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
  showNewMobileTabs?: boolean
  showMultiHotel?: boolean
}) {
  const mobileValues = useHeadlineValues(currentTab)
  const { hotelHeadline, setLocation } = useFormatHotelHeadline(mobileValues)
  const tabsContent = useMemo(() => getSearchFormTabsContent(PRODUCT_TABS), [])

  const updatedMobileValues = useMemo(
    () => ({
      ...mobileValues,
      hotels: {
        ...mobileValues.hotels,
        mobileHeadline: hotelHeadline,
      },
    }),
    [hotelHeadline, mobileValues],
  )

  const [openForm, setOpenForm] = useState(false)
  const { activeTab, changeTab } = useSearchFormTabState({ initialTab: currentTab, onFormChange })
  const formState = getProductCategory(activeTab, updatedMobileValues, setLocation, showMultiHotel)

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      fireExitMethodEvent(activeTab)
    }
    setOpenForm(isOpen)
  }
  const handleOnTabClick = (productType: ProductType) => {
    if (showNewMobileTabs) {
      fireTabClickEvent(productType)
    }
    handleOpenChange(true)
    changeTab(productType)
  }

  const handleSearchButtonClick = () => {
    fireSearchButtonClickEvent(activeTab)
    handleOpenChange(true)
  }

  const handleInputClick = () => {
    fireInputFieldEvent(activeTab)
  }

  return (
    <ComponentMetricsBoundary key={activeTab} componentId={`Mobile${COMPONENT_ID[activeTab]}`}>
      {showNewMobileTabs ? (
    <Box mb={3}>
      <SearchFormTabs
        showPanels={false}
        tabs={tabsContent}
        value={activeTab}
        onActiveTabClick={handleOnTabClick}
        onValueChange={handleOnTabClick}
      />
      <SearchFormDialog
        openForm={openForm}
        onOpenChange={handleOpenChange}
        currentTab={activeTab}
        formState={formState}
        onInputClick={handleInputClick}
      />
      <Box px={3} pt={2} pb={3}>
        <StyledButton
          width={1}
          size='extraLarge'
          borderRadius='xl'
          py={0}
          boxShadowSize='xl'
          height={56}
          onClick={() => handleSearchButtonClick()}
        >
          {tabButtons[activeTab]}
        </StyledButton>
      </Box>
    </Box>
  ) : (
    <Box mt={2} mb={3} boxShadowSize='xl' borderRadius='2xl'>
      <SearchFormTabs
        showPanels={false}
        tabs={tabsContent}
        value={activeTab}
        onActiveTabClick={handleOnTabClick}
        onValueChange={handleOnTabClick}
      />
      {activeTab === 'hotels' && <HeroMobile />}
      <SearchFormProvider>
        <Box px={3}>
          {formState.mobileHeadline && (
            <StyledHeading
              tag='h1'
              color='text.heading'
              textStyle='heading3'
              text={formState.mobileHeadline}
            />
          )}
          <Box mt={formState.mobileHeadline ? 3 : 0}>{formState.component}</Box>
        </Box>
        <Box mt={3}>{formState.bumper}</Box>
      </SearchFormProvider>
    </Box>
      )}
    </ComponentMetricsBoundary>
  )
}
